import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import AssignMealPlanDialog from './AssignMealPlanDialog';
import GenerateMealPlanDialog from './GenerateMealPlanDialog';
import { GET_ME } from '../../graphql/me';
import {
  COMPLETE_MEAL_PLAN_ASSIGNMENT,
  DELETE_MEAL_PLAN_ASSIGNMENT,
  GET_MEAL_PLAN_ASSIGNMENTS,
  GET_NUTRITION_PREFERENCES,
  STOP_MEAL_PLAN_ASSIGNMENT,
} from '../../graphql/nutrition';

const formatDate = (date) => {
  if (!date) {
    return 'Not set';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
};

const getStatusColor = (status) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'COMPLETED':
      return 'primary';
    case 'STOPPED':
      return 'default';
    default:
      return 'default';
  }
};

const NutritionTab = ({ user }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  const { loading: meLoading, error: meError, data: meData } = useQuery(GET_ME);
  const trainerId = meData?.me?._id;

  const {
    loading,
    error,
    data,
    refetch: refetchAssignments,
  } = useQuery(GET_MEAL_PLAN_ASSIGNMENTS, {
    variables: {
      input: {
        trainerId,
        userId: user?._id,
        limit: 50,
      },
    },
    skip: !trainerId || !user?._id,
  });
  const {
    loading: preferencesLoading,
    error: preferencesError,
    data: preferencesData,
  } = useQuery(GET_NUTRITION_PREFERENCES, {
    variables: { input: { userId: user?._id } },
    skip: !user?._id,
  });

  const [stopMealPlanAssignment, { loading: stopping }] = useMutation(
    STOP_MEAL_PLAN_ASSIGNMENT,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: () => {
        enqueueSnackbar('Meal plan assignment stopped.', {
          variant: 'success',
        });
        refetchAssignments();
      },
    }
  );

  const [completeMealPlanAssignment, { loading: completing }] = useMutation(
    COMPLETE_MEAL_PLAN_ASSIGNMENT,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: () => {
        enqueueSnackbar('Meal plan assignment completed.', {
          variant: 'success',
        });
        refetchAssignments();
      },
    }
  );

  const [deleteMealPlanAssignment, { loading: deleting }] = useMutation(
    DELETE_MEAL_PLAN_ASSIGNMENT,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: () => {
        enqueueSnackbar('Meal plan deleted.', {
          variant: 'success',
        });
        refetchAssignments();
      },
    }
  );

  const handleAssignmentDialogClose = (assignment) => {
    setAssignDialogOpen(false);
    if (assignment?._id) {
      refetchAssignments();
    }
  };

  const handleGenerateDialogClose = (assignment) => {
    setGenerateDialogOpen(false);
    if (assignment?._id) {
      refetchAssignments();
    }
  };

  const handleStop = (assignment) => {
    if (!window.confirm('Stop this meal plan assignment?')) {
      return;
    }

    stopMealPlanAssignment({ variables: { id: assignment._id } });
  };

  const handleComplete = (assignment) => {
    if (!window.confirm('Mark this meal plan assignment complete?')) {
      return;
    }

    completeMealPlanAssignment({ variables: { id: assignment._id } });
  };

  const handleDelete = (assignment) => {
    if (
      !window.confirm(
        'Delete this meal plan? This removes the assignment and the meal plan.'
      )
    ) {
      return;
    }

    deleteMealPlanAssignment({ variables: { id: assignment._id } });
  };

  if (meError) {
    return <ServerError message={meError.message || 'Failed to load'} />;
  }

  if (error) {
    return <ServerError message={error.message || 'Failed to load'} />;
  }

  if (preferencesError) {
    return (
      <ServerError message={preferencesError.message || 'Failed to load'} />
    );
  }

  if (
    meLoading ||
    loading ||
    preferencesLoading ||
    !data?.mealPlanAssignments
  ) {
    return <Loading />;
  }

  const assignments = data.mealPlanAssignments || [];
  const activeAssignments = assignments.filter(
    (assignment) => assignment.status === 'ACTIVE'
  );
  const historicalAssignments = assignments.filter(
    (assignment) => assignment.status !== 'ACTIVE'
  );
  const actionLoading = stopping || completing || deleting;
  const nutritionPreferences = preferencesData?.getNutritionPreferences;

  return (
    <Box mt={3}>
      <MacroFields nutritionPreferences={nutritionPreferences} />

      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
        <Button
          color="secondary"
          onClick={() => setGenerateDialogOpen(true)}
          variant="contained"
        >
          Generate meal plan
        </Button>
        <Button
          color="primary"
          onClick={() => setAssignDialogOpen(true)}
          variant="outlined"
        >
          Assign existing meal plan
        </Button>
      </Stack>

      <AssignMealPlanDialog
        open={assignDialogOpen}
        onClose={handleAssignmentDialogClose}
        trainerId={trainerId}
        user={user}
      />
      <GenerateMealPlanDialog
        open={generateDialogOpen}
        onClose={handleGenerateDialogClose}
        user={user}
      />

      {assignments.length === 0 && (
        <Typography variant="h4" color="textSecondary" sx={{ mb: 2 }}>
          No meal plans have been assigned to this user yet.
        </Typography>
      )}

      {activeAssignments.length > 0 && (
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            Active Meal Plans
          </Typography>
          <AssignmentTable
            assignments={activeAssignments}
            actionLoading={actionLoading}
            onComplete={handleComplete}
            onDelete={handleDelete}
            onStop={handleStop}
          />
        </Box>
      )}

      {historicalAssignments.length > 0 && (
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            Assignment History
          </Typography>
          <AssignmentTable
            assignments={historicalAssignments}
            actionLoading={actionLoading}
            onComplete={handleComplete}
            onDelete={handleDelete}
            onStop={handleStop}
          />
        </Box>
      )}
    </Box>
  );
};

const MacroFields = ({ nutritionPreferences }) => {
  const macros = nutritionPreferences?.macroNutrientsPerDay || {};
  const hasMacros = [
    macros.calories,
    macros.proteinGrams,
    macros.carbGrams,
    macros.fatGrams,
  ].some((value) => value != null && value !== '');

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h4" gutterBottom>
          Macros
        </Typography>
        {!hasMacros && (
          <Typography color="text.secondary" variant="body1">
            Ask the customer to enter in their macros information in the app
          </Typography>
        )}
        {hasMacros && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                label="Calories"
                value={macros.calories ?? ''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                label="Protein grams"
                value={macros.proteinGrams ?? ''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                label="Carb grams"
                value={macros.carbGrams ?? ''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                label="Fat grams"
                value={macros.fatGrams ?? ''}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const AssignmentTable = ({
  assignments,
  actionLoading,
  onComplete,
  onDelete,
  onStop,
}) => (
  <Card>
    <CardContent>
      <Box minWidth={760}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Meal Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((assignment) => {
              const mealPlan = assignment.mealPlan;
              const mealPlanUrl = mealPlan?._id
                ? `/trainer/meal_plans/${mealPlan._id}`
                : null;
              const isActive = assignment.status === 'ACTIVE';

              return (
                <TableRow key={assignment._id} hover>
                  <TableCell>
                    {mealPlanUrl ? (
                      <Link
                        color="inherit"
                        component={RouterLink}
                        to={mealPlanUrl}
                        variant="h6"
                      >
                        {mealPlan?.title || 'Untitled meal plan'}
                      </Link>
                    ) : (
                      mealPlan?.title || assignment.mealPlanId || 'Unknown'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={getStatusColor(assignment.status)}
                      label={assignment.status || 'UNKNOWN'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(assignment.assignedAt)}</TableCell>
                  <TableCell>{formatDate(assignment.startedAt)}</TableCell>
                  <TableCell>{formatDate(assignment.completedAt)}</TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {mealPlanUrl && (
                        <Button
                          component={RouterLink}
                          size="small"
                          to={mealPlanUrl}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        disabled={!isActive || actionLoading}
                        onClick={() => onComplete(assignment)}
                        size="small"
                      >
                        Complete
                      </Button>
                      <Button
                        color="error"
                        disabled={!isActive || actionLoading}
                        onClick={() => onStop(assignment)}
                        size="small"
                      >
                        Stop
                      </Button>
                      <Button
                        color="error"
                        disabled={actionLoading}
                        onClick={() => onDelete(assignment)}
                        size="small"
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </CardContent>
  </Card>
);

export default NutritionTab;
